/**
 * URL Routing Utilities
 * 
 * Provides URL path generation and parsing for deep-linking support.
 * 
 * URL Structure:
 * - /c/:campaignId/:viewMode?    - Campaign (sprint) views
 * - /t/:templateId/:viewMode?    - Template views
 * - /v/:viewId/:viewMode?        - Saved multi-campaign views
 * - /docs                        - Doc library (separate area, like Confluence Spaces)
 *                                  (/sops still parses as a legacy alias)
 * - /dashboards                  - Dashboards area
 * - /campaigns                   - Campaigns home (nothing particular open)
 * - /settings                    - Settings page
 * - /new                         - Blank new-tab page
 * - /                            - Home (redirects to last campaign or first available)
 */

// ═══════════════════════════════════════════════════════════════
// ROUTE PATTERNS
// ═══════════════════════════════════════════════════════════════

export const ROUTES = {
  HOME: '/',
  NEW_TAB: '/new',
  CAMPAIGN: '/c/:id/:viewMode?',
  TEMPLATE: '/t/:id/:viewMode?',
  VIEW: '/v/:id/:viewMode?',
  DOCS: '/docs',
  DASHBOARDS: '/dashboards',
  CAMPAIGNS: '/campaigns',
  SETTINGS: '/settings',
};

// Valid view modes for campaigns/templates
export const VIEW_MODES = ['flow', 'kanban', 'table', 'gantt', 'todolist', 'neural', 'board'];
export const DEFAULT_VIEW_MODE = 'flow';

// ═══════════════════════════════════════════════════════════════
// PATH BUILDERS
// ═══════════════════════════════════════════════════════════════

/**
 * Build a URL path for a campaign
 * @param {string} campaignId - The campaign ID
 * @param {string} [viewMode] - Optional view mode (flow, kanban, table, etc.)
 * @param {object} [options] - Additional options
 * @param {string} [options.missionId] - Mission to focus on (added as query param)
 * @param {string} [options.customViewId] - Custom view ID (added as query param)
 */
export function buildCampaignPath(campaignId, viewMode = null, options = {}) {
  if (!campaignId) return '/';
  
  let path = `/c/${campaignId}`;
  if (viewMode && VIEW_MODES.includes(viewMode)) {
    path += `/${viewMode}`;
  }
  
  // Add query params if needed
  const params = new URLSearchParams();
  if (options.missionId) {
    params.set('mission', options.missionId);
  }
  if (options.customViewId) {
    params.set('cv', options.customViewId);
  }
  
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

/**
 * Build a URL path for a template
 */
export function buildTemplatePath(templateId, viewMode = null) {
  if (!templateId) return '/';
  
  let path = `/t/${templateId}`;
  if (viewMode && VIEW_MODES.includes(viewMode)) {
    path += `/${viewMode}`;
  }
  return path;
}

/**
 * Build a URL path for a saved view (multi-campaign)
 */
export function buildViewPath(viewId, viewMode = null) {
  if (!viewId) return '/';
  
  let path = `/v/${viewId}`;
  if (viewMode && VIEW_MODES.includes(viewMode)) {
    path += `/${viewMode}`;
  }
  return path;
}

/**
 * Build settings path
 */
export function buildSettingsPath() {
  return '/settings';
}

/**
 * Build Doc library path
 */
export function buildDocsPath() {
  return '/docs';
}

/**
 * Build Dashboards path
 */
export function buildDashboardsPath() {
  return '/dashboards';
}

export function buildCampaignsPath() {
  return '/campaigns';
}

/**
 * Build blank new-tab path
 */
export function buildNewTabPath() {
  return '/new';
}

// ═══════════════════════════════════════════════════════════════
// PATH PARSERS
// ═══════════════════════════════════════════════════════════════

/**
 * Parse a URL path into navigation state
 * @param {string} pathname - The URL pathname
 * @param {string} search - The URL search string (query params)
 * @returns {object} Parsed navigation state
 */
export function parseUrlPath(pathname, search = '') {
  const params = new URLSearchParams(search);
  
  // Campaign route: /c/:id/:viewMode?
  const campaignMatch = pathname.match(/^\/c\/([^/]+)(?:\/([^/]+))?$/);
  if (campaignMatch) {
    const [, id, viewMode] = campaignMatch;
    return {
      type: 'campaign',
      id,
      viewMode: VIEW_MODES.includes(viewMode) ? viewMode : null,
      missionId: params.get('mission') || null,
      customViewId: params.get('cv') || null,
    };
  }
  
  // Template route: /t/:id/:viewMode?
  const templateMatch = pathname.match(/^\/t\/([^/]+)(?:\/([^/]+))?$/);
  if (templateMatch) {
    const [, id, viewMode] = templateMatch;
    return {
      type: 'template',
      id,
      viewMode: VIEW_MODES.includes(viewMode) ? viewMode : null,
    };
  }
  
  // Saved view route: /v/:id/:viewMode?
  const viewMatch = pathname.match(/^\/v\/([^/]+)(?:\/([^/]+))?$/);
  if (viewMatch) {
    const [, id, viewMode] = viewMatch;
    return {
      type: 'view',
      id,
      viewMode: VIEW_MODES.includes(viewMode) ? viewMode : null,
    };
  }
  
  // Doc library route (/sops kept as a legacy alias from before the rename)
  if (pathname === '/docs' || pathname === '/sops') {
    return { type: 'docs' };
  }

  if (pathname === '/dashboards') {
    return { type: 'dashboards' };
  }

  if (pathname === '/campaigns') {
    return { type: 'campaigns' };
  }

  // Settings route
  if (pathname === '/settings') {
    return { type: 'settings' };
  }

  // Blank new-tab page
  if (pathname === '/new') {
    return { type: 'blank' };
  }
  
  // Home or unknown route
  return { type: 'home' };
}

/**
 * Check if two paths represent the same location (ignoring view mode)
 */
export function isSameLocation(path1, path2) {
  const parsed1 = parseUrlPath(path1);
  const parsed2 = parseUrlPath(path2);
  
  return parsed1.type === parsed2.type && parsed1.id === parsed2.id;
}

/**
 * Get the type and ID from a path for tab matching
 */
export function getLocationKey(pathname) {
  const parsed = parseUrlPath(pathname);
  if (parsed.type === 'campaign' || parsed.type === 'template' || parsed.type === 'view') {
    return `${parsed.type}:${parsed.id}`;
  }
  return parsed.type;
}
